import { computed, inject, toRaw } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';

import {
  GetAuthoringStarted,
  GetDisplayReferencesOnDocumentLevel,
  GetExpandedFilters,
  GetFootnotes,
  GetProductAndCountryFilter,
  GetTacticContextFilters,
} from '~/constants/getters';
import {
  ResetTacticContextFilter,
  SetDisplayReferencesOnDocumentLevel,
  SetExpandedFilters,
  SetFootnotes,
  SetIsAuthoringStarted,
  SetProductAndCountryFilter,
  SetTacticContextFilter,
  SetSelectedValues,
  SetConversationScope,
} from '~/constants/actions';
import { Routes } from '~/constants/routes';
import { apiGatewayEndpoints } from '~/services/arcane-api-gateway/api-gateway-endpoints';
import { mapTacticToTacticType } from '~/utils/editor-utils';

export default () => {
  const store = useStore();
  const arcaneApiGateway = inject('arcaneApiGateway');
  const route = useRoute();

  const isAuthoringStarted = computed(() => store.getters[`authoringStore/${GetAuthoringStarted}`]);
  const expandedFilters = computed(() => store.getters[`authoringStore/${GetExpandedFilters}`]);
  const tacticContextFilters = computed(() => store.getters[`authoringStore/${GetTacticContextFilters}`]);
  const productAndCountryFilters = computed(() => store.getters[`authoringStore/${GetProductAndCountryFilter}`]);
  const isAuthoringPage = computed(() => route.name === Routes.Authoring.name);
  const footnotes = computed(() => store.getters[`authoringStore/${GetFootnotes}`]);
  const displayReferencesOnDocumentLevel = computed(
    () => store.getters[`authoringStore/${GetDisplayReferencesOnDocumentLevel}`],
  );

  const setIsAuthoringStarted = (data) => store.dispatch(`authoringStore/${SetIsAuthoringStarted}`, data);
  const setExpandedFilters = (data) => store.dispatch(`authoringStore/${SetExpandedFilters}`, data);
  const setTacticContextFilters = (data) => store.dispatch(`authoringStore/${SetTacticContextFilter}`, data);
  const resetTacticContextFilters = (data) => store.dispatch(`authoringStore/${ResetTacticContextFilter}`, data);
  const setProductAndCountryFilters = (data) => store.dispatch(`authoringStore/${SetProductAndCountryFilter}`, data);
  const setFootnotes = (data) => store.dispatch(`authoringStore/${SetFootnotes}`, data);
  const setDisplayReferencesOnDocumentLevel = (data) =>
    store.dispatch(`authoringStore/${SetDisplayReferencesOnDocumentLevel}`, data);

  const getTacticTypeAndTacticsByRegion = async (regionId) => {
    const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.tacticContexts.tacticContextTypes(regionId));
    return data;
  };

  const getTacticContextById = async (id) => {
    const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.tacticContexts.getTacticContextById(id));

    if (data.length) {
      return data[0];
    }

    return null;
  };

  const getProductAndCountryFilters = async () => {
    const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.filters.conversationFilters, {
      type: 'document',
    });

    return data?.filters?.filter((item) => item.id === 'metadata.product' || item.id === 'metadata.country') || [];
  };

  const getRegions = async () => {
    const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.tacticContexts.regions);

    return data;
  };

  const getTacticContexts = async (id, region) => {
    await arcaneApiGateway.get(apiGatewayEndpoints.tacticContexts.tacticContexts, {
      id,
      region,
    });
  };

  const handleAuthoringMetadata = async (conversation, currentCollections) => {
  
      const { messages = [] } = conversation;
      const lastMessageOfConversation = messages[conversation.messages.length - 1];

      if (lastMessageOfConversation) {
        await Promise.all(
          (lastMessageOfConversation.tactic_context || []).map(async (item) => {
            const tacticById = await getTacticContextById(item.id);

            const tacticContextTypes = await getTacticTypeAndTacticsByRegion(tacticById.regionId);

            const selectedTacticContextType = tacticContextTypes.find((tact) =>
              tact.tacticContexts.find((it) => it.id === item.id),
            );
            const selectedTacticContextTypeWithDesciption = mapTacticToTacticType([selectedTacticContextType]);

            setTacticContextFilters({
              contextTypeId: tacticById.contextTypeId,
              tacticContext: {
                ...tacticById,
                regionDescriptionName:
                  selectedTacticContextTypeWithDesciption[tacticById.contextTypeId][0].regionDescriptionName,
              },
            });
            return { [tacticById.contextTypeId]: tacticById };
          }),
        );

        const currentCollection = currentCollections.find((collection) => {
          return toRaw(collection).collectionId.toString() === lastMessageOfConversation.collection_id?.collectionId?.toString();
        });

        if (currentCollection) {
          store.dispatch(`conversationStore/${SetConversationScope}`, { collection: currentCollection })
          await store.dispatch(`authoringStore/${SetSelectedValues}`, {
            id: 'collection',
            value: lastMessageOfConversation.collection_id?.collectionId,
          });
        }

        if (
          lastMessageOfConversation.filters.find(
            (item) => item.key === 'metadata.product' || item.key === 'metadata.country',
          )
        ) {
          const productAndCountryFilters = lastMessageOfConversation.filters.reduce((acc, item) => {
            if (item.key === 'metadata.product' || item.key === 'metadata.country') {
              acc[item.key] = [...item.value];
            }
            return acc;
          }, {});

          setProductAndCountryFilters(productAndCountryFilters);
        }
      setExpandedFilters(false);
      setIsAuthoringStarted(true);
    }
   
  };

  return {
    isAuthoringStarted,
    isAuthoringPage,
    expandedFilters,
    tacticContextFilters,
    footnotes,
    displayReferencesOnDocumentLevel,
    productAndCountryFilters,
    setExpandedFilters,
    setIsAuthoringStarted,
    setTacticContextFilters,
    getTacticTypeAndTacticsByRegion,
    getRegions,
    getTacticContexts,
    resetTacticContextFilters,
    getTacticContextById,
    setFootnotes,
    setDisplayReferencesOnDocumentLevel,
    setProductAndCountryFilters,
    getProductAndCountryFilters,
    handleAuthoringMetadata,
  };
};
